// frontend/src/components/preview/PreviewToolbar.tsx
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon, ArrowDownTrayIcon, ArrowPathIcon, DocumentTextIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { TemplateItem } from '../../types';

interface PreviewToolbarProps {
  projectId?: string;
  projectName?: string;
  templates: TemplateItem[];
  selectedTemplateId: number | null;
  onTemplateChange: (templateId: number) => void; // 0 = 无模板
  onRefresh: () => void;
  onExport: () => void;
  onBack: () => void;
  isLoading: boolean;
  // Skill + RAG
  skills: { id: number; name: string }[];
  selectedSkillId: number | null;
  onSkillChange: (id: number | null) => void;
  useRAG: boolean;
  onRAGChange: (v: boolean) => void;
  // 生图引擎
  imageMode: 'none' | 'mermaid';
  onImageModeChange: (m: 'none' | 'mermaid') => void;
  // 图表风格（作用于 mermaid）
  diagramStyle: string;
  onDiagramStyleChange: (v: string) => void;
  diagramStyles: { id: number; name: string }[];  // 作为风格预设
}

interface AutoSelectOption {
  value: string;
  label: string;
}

/**
 * 宽度自适应下拉框：用与选中项同字号同内边距的隐形占位符撑开容器，
 * 真实 select 绝对定位填满容器，宽度始终等于当前选中内容（而非最长选项）。
 */
const AutoSizeSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: AutoSelectOption[];
  title?: string;
}> = ({ value, onChange, options, title }) => {
  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';
  return (
    <span className="relative inline-block" title={title}>
      <span
        aria-hidden
        className="invisible inline-block whitespace-nowrap pl-2 pr-6 py-1.5 text-xs border border-transparent"
      >
        {selectedLabel}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full appearance-none cursor-pointer pl-2 pr-6 py-1.5 text-xs border border-border rounded bg-background text-muted-foreground [background-image:none] focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
    </span>
  );
};

const PreviewToolbar: React.FC<PreviewToolbarProps> = ({
  projectId,
  projectName,
  templates,
  selectedTemplateId,
  onTemplateChange,
  onRefresh,
  onExport,
  onBack,
  isLoading,
  skills,
  selectedSkillId,
  onSkillChange,
  useRAG,
  onRAGChange,
  imageMode,
  onImageModeChange,
  diagramStyle,
  onDiagramStyleChange,
  diagramStyles,
}) => {
  const navigate = useNavigate();
  // 风格下拉当前值：default / random / 预设 skill id
  const styleSelectValue = diagramStyle && diagramStyle !== 'default' && diagramStyle !== 'random'
    ? `preset:${diagramStyle}` : (diagramStyle || 'default');
  const handleStyleSelect = (v: string) => {
    onDiagramStyleChange(v.startsWith('preset:') ? v.slice(7) : v);
  };

  return (
    <div className="flex items-center justify-between px-6 py-3 bg-card border-b border-border shrink-0">
      <div className="flex items-center gap-4 shrink-0">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm font-medium shrink-0">
          <ArrowLeftIcon className="w-4 h-4" />
          返回
        </button>

        {projectName && (
          <>
            <span className="w-px h-5 bg-border shrink-0" />
            <span className="text-sm font-semibold text-foreground truncate max-w-[320px]">{projectName}</span>
          </>
        )}

        <div className="flex items-center gap-2 whitespace-nowrap ml-auto">
          <DocumentTextIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground whitespace-nowrap">模板:</span>
          <select
            value={selectedTemplateId ?? '0'}
            onChange={(e) => {
              const id = parseInt(e.target.value, 10);
              onTemplateChange(id || 0);
            }}
            className="input py-1.5 px-3 text-sm min-w-[160px]"
          >
            <option value="0">无模板</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.template_name}
              </option>
            ))}
          </select>

          {/* Skill */}
          <AutoSizeSelect
            value={selectedSkillId != null ? String(selectedSkillId) : ''}
            onChange={(v) => onSkillChange(v ? parseInt(v, 10) : null)}
            options={[
              { value: '', label: '技能: 默认' },
              ...skills.map((s) => ({ value: String(s.id), label: s.name })),
            ]}
          />

          {/* 联网检索 */}
          <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={useRAG} onChange={(e) => onRAGChange(e.target.checked)} className="rounded" />
            联网检索
          </label>

          {/* 生图引擎 */}
          <AutoSizeSelect
            value={imageMode}
            onChange={(v) => onImageModeChange(v as 'none' | 'mermaid')}
            title="生图引擎：不生图 / Mermaid"
            options={[
              { value: 'none', label: '生图: 不生图' },
              { value: 'mermaid', label: '生图: Mermaid' },
            ]}
          />

          {/* 图表风格（仅 mermaid 生效） */}
          {imageMode === 'mermaid' && (
            <AutoSizeSelect
              value={styleSelectValue}
              onChange={handleStyleSelect}
              title="图表视觉风格：随机 / 默认 / 预设（在后台用『生图』类别技能自定义）"
              options={[
                { value: 'random', label: '风格: 随机' },
                { value: 'default', label: '风格: 默认' },
                ...diagramStyles.map((s) => ({ value: `preset:${s.id}`, label: s.name })),
              ]}
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onRefresh} disabled={isLoading} className="btn-ghost text-sm">
          <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          刷新
        </button>
        <button onClick={onExport} disabled={isLoading} className="btn-ghost text-sm">
          <ArrowDownTrayIcon className="w-4 h-4" />
          导出
        </button>
      </div>
    </div>
  );
};

export default PreviewToolbar;
